import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DataTableComponent from 'app_components/commons/dataTable';
import { useSelector } from 'react-redux';
import { CombinedReducers } from 'app_events/index';
import SchedulingService from 'app_services/campus/academic/schedulingService';
import moment from 'moment';
import LoadingWrapper from 'app_components/commons/LoadingWrapper/LoadingWrapper';
import StudentDashboardCustomTable from '../StudentDashboardCustomTable/StudentDashboardCustomTable';
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
import TableItemsPaginator from 'app_components/modules/TableItemsPaginator/TableItemsPaginator';
import LoginMoodle from 'app_components/modules/LoginMoodle/LoginMoodle';
import { external_api } from 'app_config/globals';
import { getWebexUrlFromDescription } from 'app_utils/functions/getWebexUrlFromDescription';
import { IDataTableHeaderColId } from 'app_components/commons/dataTable/containers/dataTableCommon';
import useWindowWidth from 'app_hooks/useWindowWidth';
import AccordionTable from 'app_components/modules/AccordionTable/AccordionTable';
import TextComponent from 'app_components/commons/TextComponent/TextComponent';

interface IStudentDashboardSchedulingTableProps {
  id?: string
}

const DATE_SESSION_FORMAT = 'hh:mm a'
const YEAR_DATE_FORMAT = 'DD/MM/YYYY'
const ITEMS_PER_PAGE = 10
const NA_TEXT = 'N/A'
const CUSTON_TIME_TEXT = 'Avanza a tu ritmo'
const MOBILE_BREAKPOINT = 800

const StudentDashboardSchedulingTable: FC<IStudentDashboardSchedulingTableProps> = ({ id }) => {
  const [schedulings, setSchedulings] = useState<unknown[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { t } = useTranslation()
  const { courseSelected } = useSelector((state: CombinedReducers) => state.courses)
  const isVirtual = useMemo(() => courseSelected?.schedulingMode?.name === 'Virtual', [courseSelected])
  const withoutTutor = useMemo(() => courseSelected?.withoutTutor, [courseSelected])
  const isOnLine = useMemo(() => courseSelected?.schedulingMode?.name === 'En linea', [courseSelected])
  const { windowWidth } = useWindowWidth()

  const schedulingService = useMemo(() => new SchedulingService(), [])

  const headers = useMemo(() => [
    `${t('modules.scheduling_module.headers.course')}`,
    `${t('modules.scheduling_module.headers.course_join')}`,
    isVirtual ? 'Fecha de inicio' : `${t('modules.scheduling_module.headers.start')}`,
    ...(isVirtual ? ['Fecha de fin'] : []),
    `${t('modules.scheduling_module.headers.duration')}`,
    `${t('modules.scheduling_module.headers.schedule')}`,
    ...(!withoutTutor ? [`${t('modules.scheduling_module.headers.location')}`] : []),
  ], [t, isVirtual])

  const headerKeys = useMemo(() => ([
    'course_name',
    'component_render_webex',
    'start_date',
    ...(isVirtual ? ['end_date'] : []),
    'duration',
    'schedule',
    ...(!withoutTutor ? ['address'] : ''),
  ]), [isVirtual])

  const tooltipKeys = useMemo(() => ['course_name'], [])

  const headerIds = useMemo((): IDataTableHeaderColId[] => [
    {
      index: 1,
      id: 'student-dashboard-scheduling-table-join'
    }
  ], [])

  const getSchedulingData = useCallback(async () => {
    if (!courseSelected?._id) {
      setSchedulings([])
      return
    }
    setIsLoading(true)
    const dataResponse = await schedulingService.fetchSchedulingData({
      moodle_id: courseSelected?._id,
      courseSchedulingId: courseSelected?.courseScheduling,
    }, {showError: false})
    if (dataResponse) {
      if (dataResponse.scheduling?.length) {
        setSchedulings([...dataResponse.scheduling])
      } else {
        setSchedulings([])
      }
    }
    setIsLoading(false)
  }, [schedulingService, courseSelected])

  useEffect(() => {
    getSchedulingData()
  }, [getSchedulingData])

  const getScheduleString = useCallback((startDate: string, duration: number) => {
    if (withoutTutor) return CUSTON_TIME_TEXT
    if (isVirtual) return NA_TEXT
    if (!startDate) return ' - '
    const start = moment(startDate).format(DATE_SESSION_FORMAT)
    const end = moment(startDate).add(duration, 'seconds').format(DATE_SESSION_FORMAT)
    return `${start} a ${end}`
  }, [isVirtual])

  const getWebexButton = useCallback((event) => {
    let url = event?.webex?.url
    if (!url) {
      url = getWebexUrlFromDescription(event?.webex?.description)
    }
    const today = moment()
    const startDate = moment(event.startDate).subtract(1, 'day')
    const endDate = moment(startDate).add(event.durationOnSeconds, 'seconds').add(1, 'hour')
    const isDisabled = !url || !(today.isBetween(startDate, endDate))

    return (
      <LoginMoodle redirectTo={url} targetBlank>
        <ButtonComponent
          disabled={isDisabled}
          variant="secondary"
          width="100%"
          textTransform='capitalize'
          borderRadius="20px"
          padding="2px 10px"
          type="button"
        >
          Webex
        </ButtonComponent>
      </LoginMoodle>
    )
  }, [])

  const getLoginButton = useCallback(() => {
    return (
      <LoginMoodle targetBlank redirectTo={`${external_api?.moodle?.url}/course/view.php?id=${courseSelected?._id}`}>
        <ButtonComponent
          variant="secondary"
          width="100%"
          textTransform='initial'
          borderRadius="20px"
          padding="2px 10px"
          type="button"
        >
          Ir a cursos
        </ButtonComponent>
      </LoginMoodle>
    )
  }, [courseSelected])

  const isSessionFinished = useCallback((startDate: string, duration: number, endDate?: string): boolean => {
    if (!startDate) return true
    if (isVirtual) {
      if (!endDate) return false
      const formattedEndDate = moment(endDate).utc().format('YYYY/MM/DD')
      const end = moment(formattedEndDate).set('hours', 23).set('minutes', 59)
      return moment().isAfter(end)
    } else {
      const end = moment(startDate).add(duration, 'seconds')
      return moment().isAfter(end)
    }
  }, [isVirtual])

  const schedulingData = useMemo(() => {
    if (schedulings?.length) {
      return schedulings
        ?.filter((scheduling: any) => !isSessionFinished(withoutTutor ? courseSelected?.startDate : scheduling.startDate, scheduling.durationOnSeconds, withoutTutor ? courseSelected?.endDate : scheduling.endDate))
        ?.map((scheduling: any) => ({
          ...scheduling,
          schedule: getScheduleString(scheduling.startDate, scheduling.durationOnSeconds),
          component_render_webex: isOnLine ? (scheduling?.webex ? getWebexButton(scheduling) : null) : getLoginButton(),
          address: !isVirtual ? scheduling?.address : NA_TEXT,
          start_date: withoutTutor ? moment(courseSelected?.startDate).format(YEAR_DATE_FORMAT) : moment(scheduling.startDate).format(YEAR_DATE_FORMAT),
          end_date: withoutTutor ? moment(courseSelected?.endDate).format(YEAR_DATE_FORMAT) : moment(scheduling.endDate).format(YEAR_DATE_FORMAT)
        }))
    } else {
      return []
    }
  }, [schedulings, getWebexButton, isVirtual, getScheduleString, isOnLine, getLoginButton, isSessionFinished])

  return (
    <div id={id} className="student-dashboard-scheduling-table--layout">
      <LoadingWrapper loading={isLoading} height="80px">
        <TableItemsPaginator items={schedulingData} perPage={ITEMS_PER_PAGE}>
          {(itemsToRender) => (
            windowWidth && windowWidth > MOBILE_BREAKPOINT ? (
              <StudentDashboardCustomTable>
                <DataTableComponent
                  data={itemsToRender}
                  headerKeys={headers}
                  keys={headerKeys}
                  tooltipKeys={tooltipKeys}
                  detailsKey={[]}
                  onModal={null}
                  headerIds={headerIds}
                />
              </StudentDashboardCustomTable>
            ) : (
              <AccordionTable
                id={`${id}-accordion-table`}
                items={itemsToRender}
                renderContent={(item) => (
                  <div>
                    <ul>
                      <li>
                        <TextComponent>
                          <strong>{t('modules.scheduling_module.headers.course')}:</strong> {item.course_name}
                        </TextComponent>
                      </li>
                      <li>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <TextComponent>
                            <strong>{t('modules.scheduling_module.headers.course_join')}:</strong>
                          </TextComponent>
                          {item.component_render_webex}
                        </div>
                      </li>
                      <li>
                        <TextComponent>
                          <strong>{t('modules.scheduling_module.headers.start')}:</strong> {item.start_date}
                        </TextComponent>
                      </li>
                      <li>
                        <TextComponent>
                          <strong>{t('modules.scheduling_module.headers.duration')}:</strong> {item.duration}
                        </TextComponent>
                      </li>
                      <li>
                        <TextComponent>
                          <strong>{t('modules.scheduling_module.headers.schedule')}:</strong> {item.schedule}
                        </TextComponent>
                      </li>
                      <li>
                        <TextComponent>
                          <strong>{t('modules.scheduling_module.headers.location')}:</strong> {item.address}
                        </TextComponent>
                      </li>
                    </ul>
                  </div>
                )}
                renderHeader={(item) => (
                  <TextComponent fontSize="14px" elipsis>
                    {item.course_name}
                  </TextComponent>
                )}
              />
            )
          )}
        </TableItemsPaginator>
      </LoadingWrapper>
    </div>
  )
}

export default StudentDashboardSchedulingTable
